import React from 'react';
import { Trophy, TrendingUp, Users } from 'lucide-react';
import { motion } from 'framer-motion';

const RankBoard = ({ posts }) => {
  // 获取热门标签统计
  const getHotTags = () => {
    const tagStats = {};
    posts.forEach(post => {
      if (post.tags) {
        post.tags.forEach(tag => {
          if (!tagStats[tag]) {
            tagStats[tag] = { count: 0, totalReactions: 0 };
          }
          tagStats[tag].count += 1;
          tagStats[tag].totalReactions += (post.reactions?.disgusted || 0) + (post.reactions?.support || 0);
        });
      }
    });

    return Object.entries(tagStats)
      .sort(([, a], [, b]) => b.totalReactions - a.totalReactions)
      .slice(0, 5);
  };

  // 获取本周槽王
  const getTopPosts = () => {
    return [...posts]
      .sort((a, b) => (b.reactions?.disgusted || 0) - (a.reactions?.disgusted || 0))
      .slice(0, 3);
  };

  const hotTags = getHotTags();
  const topPosts = getTopPosts();

  return (
    <div className="space-y-6">
      {/* 本周槽王 */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-xl p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="font-bold text-yellow-500">本周槽王</h3>
        </div>

        <div className="space-y-3">
          {topPosts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-3 rounded-lg border ${index === 0
                  ? 'bg-yellow-900 border-yellow-500'
                  : index === 1
                    ? 'bg-gray-700 border-gray-400'
                    : 'bg-orange-900 border-orange-600'
                }`}
            >
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${index === 0 ? 'bg-yellow-500 text-slate-900' :
                    index === 1 ? 'bg-gray-400 text-slate-900' :
                      'bg-orange-600 text-white'
                  }`}>
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-300 line-clamp-2 mb-1">
                    {post.content.substring(0, 50)}...
                  </p>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      🔥
                      {post.reactions?.disgusted || 0}
                    </span>
                    {index === 0 && (
                      <span className="bg-red-900 text-red-400 px-2 py-0.5 rounded text-xs">
                        职场重灾区
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* 热门标签 */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-slate-800 border border-slate-700 rounded-xl p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="w-5 h-5 text-terminal-green" />
          <h3 className="font-bold text-terminal-green">热门标签</h3>
        </div>

        <div className="space-y-2">
          {hotTags.map(([tag, stats], index) => (
            <motion.div
              key={tag}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-700 transition-all"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-300">#{tag}</span>
                <span className="text-xs text-slate-500">({stats.count}条)</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-orange-400">
                🔥
                <span>{stats.totalReactions}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* 实时统计 */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-slate-800 border border-slate-700 rounded-xl p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <Users className="w-5 h-5 text-blue-400" />
          <h3 className="font-bold text-blue-400">实时统计</h3>
        </div>

        <div className="space-y-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-terminal-green">
              {posts.length}
            </div>
            <div className="text-xs text-slate-500">总吐槽数</div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-warning-orange">
              {posts.reduce((sum, post) => sum + (post.reactions?.disgusted || 0), 0)}
            </div>
            <div className="text-xs text-slate-500">恶心爆了总数</div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">
              {posts.reduce((sum, post) => sum + (post.reactions?.support || 0), 0)}
            </div>
            <div className="text-xs text-slate-500">拥抱同行人总数</div>
          </div>
        </div>
      </motion.div>

      {/* 今日金句 */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-gradient-to-r from-slate-800 to-slate-700 border border-slate-600 rounded-xl p-6"
      >
        <h3 className="font-bold text-terminal-green mb-3 text-center">💡 今日金句</h3>
        <blockquote className="text-sm text-slate-300 italic text-center leading-relaxed">
          "不是我们太脆弱，<br />是职场太荒诞。"
        </blockquote>
        <div className="text-center mt-3">
          <span className="text-xs text-slate-500">— 某匿名打工人</span>
        </div>
      </motion.div>
    </div>
  );
};

export default RankBoard;